import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
  FinanceAdminSliceInitialState,
  APIResponse,
  FinancePayment,
  GetFinancePaymentsRequestPayload,
  GetFinancePaymentsSuccessResponse,
  HospitalConsultationCase,
  RefundPaymentRequestPayload,
  RefundPaymentSuccessResponse,
  GetSpecialistPayoutsRequestPayload,
  GetSpecialistPayoutsSuccessResponse,
  ChangePayoutStatusRequestPayload,
  ChangePayoutStatusSuccessResponse,
} from '@/interface';

const initialState: FinanceAdminSliceInitialState = {
  isLoading: false,
  refundLoading: false,
  changePayoutStatusLoading: false,
  financeAdminSuccess: false,
  financeAdminFailure: false,
  payments: {
    payments: [],
    next_page: null,
    current_page: 0,
    total_pages: 0,
    total_results: 0,
  },
  specialistPayouts: {
    payouts: [],
    next_page: null,
    current_page: 0,
    total_pages: 0,
    total_results: 0,
  },
};

const financeAdminSlice = createSlice({
  name: 'financeAdmin',
  initialState,
  reducers: {
    /**
     * get payments list
     */
    getFinancePaymentsRequest: (state, action: PayloadAction<GetFinancePaymentsRequestPayload>) => {
      state.isLoading = true;
      state.financeAdminSuccess = false;
      state.financeAdminFailure = false;
    },
    getFinancePaymentsSuccess: (state, action: PayloadAction<GetFinancePaymentsSuccessResponse>) => {
      state.isLoading = false;
      state.financeAdminSuccess = action.payload.status_code;
      state.payments.payments = action.payload.data.payments;
      state.payments.next_page = action.payload.data.next_page;
      state.payments.current_page = action.payload.data.current_page;
      state.payments.total_pages = action.payload.data.total_page;
      state.payments.total_results = action.payload.data.total_result;
    },
    getFinancePaymentsFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.financeAdminFailure = action.payload.status_code;
      if (['E-10044', 'E-10054'].includes(action.payload.status_code)) {
        state.payments.payments = [];
        state.payments.next_page = null;
        state.payments.current_page = 0;
        state.payments.total_pages = 0;
        state.payments.total_results = 0;
      }
    },

    /**
     * refund payment
     */
    refundPaymentRequest: (state, action: PayloadAction<RefundPaymentRequestPayload>) => {
      state.refundLoading = true;
      state.financeAdminSuccess = false;
      state.financeAdminFailure = false;
    },
    refundPaymentSuccess: (state, action: PayloadAction<RefundPaymentSuccessResponse>) => {
      state.refundLoading = false;
      state.financeAdminSuccess = action.payload.status_code;
    },
    refundPaymentFailure: (state, action: PayloadAction<APIResponse>) => {
      state.refundLoading = false;
      state.financeAdminFailure = action.payload.status_code;
    },

    /**
     * get specialist payouts list
     */
    getSpecialistPayoutsRequest: (state, action: PayloadAction<GetSpecialistPayoutsRequestPayload>) => {
      state.isLoading = true;
      state.financeAdminSuccess = false;
      state.financeAdminFailure = false;
    },
    getSpecialistPayoutsSuccess: (state, action: PayloadAction<GetSpecialistPayoutsSuccessResponse>) => {
      state.isLoading = false;
      state.financeAdminSuccess = action.payload.status_code;
      if (!state.specialistPayouts) {
        state.specialistPayouts = {
          payouts: [],
          next_page: null,
          current_page: 0,
          total_pages: 0,
          total_results: 0,
        };
      }
      state.specialistPayouts.payouts = action.payload.data.payouts;
      state.specialistPayouts.next_page = action.payload.data.next_page;
      state.specialistPayouts.current_page = action.payload.data.current_page;
      state.specialistPayouts.total_pages = action.payload.data.total_page;
      state.specialistPayouts.total_results = action.payload.data.total_result;
    },
    getSpecialistPayoutsFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.financeAdminFailure = action.payload.status_code;
      if (['E-10044', 'E-10054'].includes(action.payload.status_code)) {
        state.specialistPayouts.payouts = [];
        state.specialistPayouts.next_page = null;
        state.specialistPayouts.current_page = 0;
        state.specialistPayouts.total_pages = 0;
        state.specialistPayouts.total_results = 0;
      }
    },

    /**
     * change payout status
     */
    changePayoutStatusRequest: (state, action: PayloadAction<ChangePayoutStatusRequestPayload>) => {
      state.changePayoutStatusLoading = true;
      state.financeAdminSuccess = false;
      state.financeAdminFailure = false;
    },
    changePayoutStatusSuccess: (state, action: PayloadAction<ChangePayoutStatusSuccessResponse>) => {
      state.changePayoutStatusLoading = false;
      state.financeAdminSuccess = action.payload.status_code;
    },
    changePayoutStatusFailure: (state, action: PayloadAction<APIResponse>) => {
      state.changePayoutStatusLoading = false;
      state.financeAdminFailure = action.payload.status_code;
    },

    /**
     * reset finance admin message
     */
    resetFinanceAdminMessage: state => {
      state.financeAdminSuccess = false;
      state.financeAdminFailure = false;
    },

    /**
     * add new payment from socket (real-time update for Finance Admin)
     */
    addFinancePaymentData: (state, action: PayloadAction<FinancePayment>) => {
      // do not add if payment already exists
      const alreadyExists = state.payments.payments.some(p => p.transaction_id === action.payload.transaction_id);
      if (alreadyExists) return;

      state.payments.payments.unshift(action.payload);
      state.payments.total_results = (state.payments.total_results || 0) + 1;
    },

    /**
     * update payment status from socket (e.g. Refund when patient cancels)
     */
    updateFinancePaymentStatus: (state, action: PayloadAction<{case_id: number; status: string}>) => {
      const {case_id, status} = action.payload;
      const index = state.payments.payments.findIndex(p => p.case_id === case_id);
      if (index !== -1) {
        state.payments.payments[index].status = status;
      }
    },

    addFinanceConsultationCaseData: (state, action: PayloadAction<HospitalConsultationCase>) => {},
  },
});

export const {
  getFinancePaymentsRequest,
  getFinancePaymentsSuccess,
  getFinancePaymentsFailure,
  refundPaymentRequest,
  refundPaymentSuccess,
  refundPaymentFailure,
  getSpecialistPayoutsRequest,
  getSpecialistPayoutsSuccess,
  getSpecialistPayoutsFailure,
  changePayoutStatusRequest,
  changePayoutStatusSuccess,
  changePayoutStatusFailure,
  resetFinanceAdminMessage,
  addFinancePaymentData,
  updateFinancePaymentStatus,
  addFinanceConsultationCaseData,
} = financeAdminSlice.actions;

export default financeAdminSlice.reducer;
